/* eslint-disable */
/**
 * Secondary Criteria Validation Functions
 * Contains all validation logic for Secondary Criteria Excel uploads
 */

import { isValidInput, isNotValidInput, transformText, appendComment } from './validationUtils';
import { CRITERIA_EFFECTIVE_TYPE } from '../../../constants/constants';
import {
  SecondaryCriteriaIndices,
  ValidationReferenceData,
  ShowFeature,
  ExcelData,
  ExcelDataRow,
  ValidationStatusFlagSetter,
  GroupedByRuleType,
  GroupedByHierarchy
} from './validationTypes';

/**
 * Helper function to group rows by rule type
 * 
 * @param data - Excel data array
 * @param indices - Column indices object
 * @returns Promise resolving to grouped records by rule type
 */
export const groupAndOrderRows = async (
  data: ExcelData,
  indices: SecondaryCriteriaIndices
): Promise<GroupedByRuleType> => {
  const { RULE_TYPE_IND } = indices;

  const groupedByRuleType = data.reduce((acc: any, record: any) => {
    if (record.length == 0) {
      return acc;
    } else {
      const ValidRuleRecords = record[RULE_TYPE_IND];
      if (!acc[ValidRuleRecords]) {
        acc[ValidRuleRecords] = [];
      }
      acc[ValidRuleRecords].push(record);
      return acc;
    }
  }, {} as GroupedByRuleType);

  return groupedByRuleType;
};

/**
 * Helper function to group records by hierarchy keys
 * 
 * @param req - Array of records to group
 * @param keys - Array of column indices to use as grouping keys
 * @returns Promise resolving to grouped records by hierarchy
 */
export const groupingHierarchy = async (
  req: ExcelDataRow[],
  keys: number[]
): Promise<GroupedByHierarchy> => {
  const groupedByHierarchy = req.reduce((acc: any, record: any) => {
    const key = keys.map((k: number) => record[k]).join('-');
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(record);
    return acc;
  }, {} as GroupedByHierarchy);

  return groupedByHierarchy;
};

/**
 * Helper function to validate table records for continuous ranges
 * 
 * @param records - Array of table records to validate
 * @param scIndices - Column indices object
 * @param validationStatusFlagSetter - Function to set validation status flag
 * @returns Promise resolving to validated records
 */
export const validateTableRecords = async (
  records: ExcelDataRow[],
  scIndices: SecondaryCriteriaIndices,
  validationStatusFlagSetter: ValidationStatusFlagSetter
): Promise<ExcelDataRow[]> => {
  const { START_RANGE_IND, END_RANGE_IND, VALIDATION_COMMENT_IND, VALIDATION_STATUS_IND } = scIndices
  records.sort((a: any, b: any) => Number(a[START_RANGE_IND]) - Number(b[START_RANGE_IND]));
  let valid = true;
  console.log('The records', records)
  if (records.length > 0) {
    const firstRecord = records[0];
    console.log('The first record', records[0])
    const lastRecord = records[records.length - 1];

    if (Number(firstRecord[START_RANGE_IND]) !== 0) {
      valid = false;
      firstRecord[VALIDATION_COMMENT_IND] = "Range must start with 0";
    }

    if (Number(lastRecord[END_RANGE_IND]) !== 9999) {
      valid = false;
      lastRecord[VALIDATION_COMMENT_IND] = "Range must end with 9999";
    }
    for (let i = 0; i < records.length; ++i) {
      const record = records[i];
      if (i > 0 && Number(records[i - 1][END_RANGE_IND]) + 1 !== Number(record[START_RANGE_IND])) {
        valid = false;
        record[VALIDATION_COMMENT_IND] = "Range should be continuous";
      }
    }
    if (!valid) {
      validationStatusFlagSetter('Validation Failed');
      records.forEach((record: any) => {
        record[VALIDATION_STATUS_IND] = 'Failed';
      });
    }
    return records
  }
  return records;
};

/**
 * Helper function to validate equation parameters
 * 
 * @param subGroupRecords - Array of equation records to validate
 * @param indices - Column indices object
 * @param referenceData - Reference data for validation
 * @param validationStatusFlagSetter - Function to set validation status flag
 * @returns Promise resolving to validated records
 */
export const validateEquationParameters = async (
  subGroupRecords: ExcelDataRow[],
  indices: SecondaryCriteriaIndices,
  referenceData: ValidationReferenceData,
  validationStatusFlagSetter: ValidationStatusFlagSetter
): Promise<ExcelDataRow[]> => {
  const { EQUATION_TYPE_IND, EQUATION_PARAM_IND, VALIDATION_STATUS_IND, VALIDATION_COMMENT_IND } = indices;
  const equationTypeMap = referenceData.equationParameter.reduce((acc: any, item: any) => {
    acc[item.equationTypeName] = item.params;
    return acc;
  }, {});

  const equationType = subGroupRecords[0][EQUATION_TYPE_IND];
  const requiredParams = equationTypeMap[equationType];

  const providedParams = subGroupRecords.map(record => record[EQUATION_PARAM_IND]);

  requiredParams && requiredParams.forEach((param: any) => {
    if (!providedParams.includes(param.parameterName)) {
      validationStatusFlagSetter('Validation Failed');
      subGroupRecords.forEach((record) => {
        record[VALIDATION_STATUS_IND] = record[VALIDATION_STATUS_IND] == undefined ? `Missing parameter ${param.parameterName} for ${equationType} equation` : record[VALIDATION_STATUS_IND] + `, Missing parameter ${param.parameterName} for ${equationType} equation`
        record[VALIDATION_COMMENT_IND] = 'Failed';
      });
    }
  });
  return subGroupRecords
};

/**
 * Main validation function for Secondary Criteria Excel data
 * 
 * @param data - Excel data array (2D array with headers in row 0)
 * @param indices - Column indices object
 * @param referenceData - Reference data for validation (rule types, criteria types, etc.)
 * @param disableSecondaryCriteriaPublish - Feature flag for end date column ('0' or '1')
 * @param showFeature - Feature access flags (equAccess, showSecondary, etc.)
 * @param validationStatusFlagSetter - Function to set validation status flag
 * @param tranformDate - Function to transform Excel dates to JavaScript Date objects
 * @returns Promise resolving to validated data array
 */
export const validateSecondaryCriteria = async (
  data: ExcelData,
  indices: SecondaryCriteriaIndices,
  referenceData: ValidationReferenceData,
  disableSecondaryCriteriaPublish: string,
  showFeature: ShowFeature,
  validationStatusFlagSetter: ValidationStatusFlagSetter,
  tranformDate: (date: any) => Date
): Promise<any> => {

  const {
    ZONE_IND, DEPARTMENT_IND, SUB_DEPARTMENT_IND, BRACKET_IND, BRAND_IND, RMS_IND,
    RULE_TYPE_IND, RULE_APP_ORDER_IND, CRITERIA_TYPE_IND, PRICE_PARAM_IND, EFFECT_TYPE_IND,
    START_RANGE_IND, END_RANGE_IND, CATEGORY_TYPE_IND, CATEGORY_SUBTYPE_IND, EQUATION_TYPE_IND,
    EQUATION_PARAM_IND, DISCOUNT_VALUE_IND, START_DATE_IND, END_DATE_IND,
    VALIDATION_STATUS_IND, VALIDATION_COMMENT_IND, UPLOAD_STATUS_IND, UPLAOD_COMMENTS_IND
  } = indices;

  const numberRegex = /^(?!.*\.\d{4})\d{1,6}(\.\d{1,3})?$/;
  const percentRegex = /^(100(\.\d{1,3}[0])?|[1-9]?[0-9](\.\d{1,3})?)$/;
  const numRegex = /^[1-9][0-9]*$/;
  var newData: any = [];
  for (let i = 1; i < data.length; i++) {
    if (data[i].length == 0)
      break
    // Build duplicate key - include END_DATE if feature flag is '0'
    let duplicateKey =
      String(data[i][ZONE_IND]) + '-' +
      String(data[i][DEPARTMENT_IND]) + '-' +
      String(data[i][SUB_DEPARTMENT_IND] ?? '') + '-' +
      String(data[i][BRACKET_IND] ?? '') + '-' +
      String(data[i][BRAND_IND] ?? '') + '-' +
      String(data[i][RMS_IND] ?? '') + '-' +
      String(data[i][RULE_TYPE_IND] ?? '') + '-' +
      // String(data[i][RULE_APP_ORDER_IND] ?? '') + '-' +
      String(data[i][CRITERIA_TYPE_IND] ?? '') + '-' +
      String(data[i][PRICE_PARAM_IND] ?? '') + '-' +
      String(data[i][EFFECT_TYPE_IND] ?? '') + '-' +
      // String(data[i][DISCOUNT_VALUE_IND] ?? '') + '-' +
      String(data[i][START_DATE_IND] ?? '') + '-' +
      String(data[i][START_RANGE_IND] ?? '') + '-' +
      String(data[i][END_RANGE_IND] ?? '') + '-' +
      String(data[i][CATEGORY_TYPE_IND] ?? '') + '-' +
      String(data[i][CATEGORY_SUBTYPE_IND] ?? '') + '-' +
      String(data[i][EQUATION_TYPE_IND] ?? '') + '-' +
      String(data[i][EQUATION_PARAM_IND] ?? '');

    // Include END_DATE in duplicate check if feature flag is '0'
    if (disableSecondaryCriteriaPublish === '0') {
      duplicateKey += '-' + String(data[i][END_DATE_IND] ?? '');
    }

    newData.push(duplicateKey);
  }

  var map: any = {};
  var duplicates = [];
  for (var i = 0; i < newData.length; i++) {
    var element = newData[i];
    if (!map[element]) {
      map[element] = [i];
    }
    else {
      map[element].push(i);
      duplicates.push(i + 1)
    }
  }
  let validationFlag: boolean = true;

  for (let rowindex = 1; rowindex < data.length; rowindex++) {
    const validateDiscount = () => {
      /* Validate the Discount Value */
      if (isValidInput(data[rowindex][RULE_TYPE_IND])) {
        if (isNotValidInput(data[rowindex][DISCOUNT_VALUE_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], `Discount Value is mandatory`);
        }
        else if (isValidInput(data[rowindex][DISCOUNT_VALUE_IND])) {


          if (isNaN(data[rowindex][DISCOUNT_VALUE_IND])) {
            validationFlag = false;
            data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
            data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], `Discount Value is InValid`);
          }
          if (!(numberRegex.test(data[rowindex][DISCOUNT_VALUE_IND]))) {
            validationFlag = false;

            data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
            data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], `Discount Value Exceeds limit numeric(6,3)`);
          }

        }
        /* Validate the percentage */
        else if ((data[rowindex][EFFECT_TYPE_IND]).toUpperCase() == CRITERIA_EFFECTIVE_TYPE[1] && !(percentRegex.test(data[rowindex][DISCOUNT_VALUE_IND]))) {
          validationFlag = false;

          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], `Discount Value Exceeds 100 when the Criteria Effect Type is 'Percentage'`);
        }
      }
    }
    console.log('The data in the row' + rowindex, data[i])
    if (data[rowindex].length == 0) {
      break;
    }
    if (isValidInput(data[rowindex][RMS_IND]) && (isValidInput(data[rowindex][BRACKET_IND]) || isValidInput(data[rowindex][SUB_DEPARTMENT_IND]) || isValidInput(data[rowindex][DEPARTMENT_IND]))) {
      validationFlag = false;
      data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
      data[rowindex][VALIDATION_COMMENT_IND] = "RMS Number does not require other hierarchy level information.";
      data[rowindex][UPLOAD_STATUS_IND] = undefined;
      data[rowindex][UPLAOD_COMMENTS_IND] = undefined;
      if (data[rowindex][START_DATE_IND] != undefined) {
        if (!(isNaN(data[rowindex][START_DATE_IND]))) {
          data[rowindex][START_DATE_IND] = tranformDate(data[rowindex][START_DATE_IND]);
        }
      }
      if (data[rowindex][END_DATE_IND] != undefined) {
        if (!(isNaN(data[rowindex][END_DATE_IND]))) {
          data[rowindex][END_DATE_IND] = tranformDate(data[rowindex][END_DATE_IND]);
        }
      }
    }
    else if (isNotValidInput(data[rowindex][DEPARTMENT_IND]) && isNotValidInput(data[rowindex][SUB_DEPARTMENT_IND]) && isNotValidInput(data[rowindex][BRACKET_IND]) && isNotValidInput(data[rowindex][RMS_IND])) {
      validationFlag = false;
      data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
      data[rowindex][VALIDATION_COMMENT_IND] = "Hierarchy information is required. Please provide the following data: Department (mandatory), Sub-department (optional), Bracket (optional), and RMS Number (optional)";
      data[rowindex][UPLOAD_STATUS_IND] = undefined;
      data[rowindex][UPLAOD_COMMENTS_IND] = undefined;
      if (data[rowindex][START_DATE_IND] != undefined) {
        if (!(isNaN(data[rowindex][START_DATE_IND]))) {
          data[rowindex][START_DATE_IND] = tranformDate(data[rowindex][START_DATE_IND]);
        }
      }
      if (data[rowindex][END_DATE_IND] != undefined) {
        if (!(isNaN(data[rowindex][END_DATE_IND]))) {
          data[rowindex][END_DATE_IND] = tranformDate(data[rowindex][END_DATE_IND]);
        }
      }
    }
    else if (isNotValidInput(data[rowindex][DEPARTMENT_IND]) && isNotValidInput(data[rowindex][RMS_IND])) {
      validationFlag = false;
      data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
      if (isValidInput(data[rowindex][BRACKET_IND] && isNotValidInput(data[rowindex][SUB_DEPARTMENT_IND]))) {
        data[rowindex][VALIDATION_COMMENT_IND] = "Department and Sub-department are required when a Bracket is provided.";
        data[rowindex][UPLOAD_STATUS_IND] = undefined;
        data[rowindex][UPLAOD_COMMENTS_IND] = undefined;
      }
      else if (isValidInput(data[rowindex][SUB_DEPARTMENT_IND])) {
        data[rowindex][VALIDATION_COMMENT_IND] = " Department is required when Sub Department is provided ";
        data[rowindex][UPLOAD_STATUS_IND] = undefined;
        data[rowindex][UPLAOD_COMMENTS_IND] = undefined;
      }
      if ((data[rowindex][START_DATE_IND]) != undefined) {
        if (!(isNaN(data[rowindex][START_DATE_IND]))) {
          data[rowindex][START_DATE_IND] = tranformDate(data[rowindex][START_DATE_IND]);
        }
      }
      if (data[rowindex][END_DATE_IND] != undefined) {
        if (!(isNaN(data[rowindex][END_DATE_IND]))) {
          data[rowindex][END_DATE_IND] = tranformDate(data[rowindex][END_DATE_IND]);
        }
      }
    }
    else if (isNotValidInput(data[rowindex][SUB_DEPARTMENT_IND]) && isValidInput(data[rowindex][BRACKET_IND])) {
      validationFlag = false;
      data[rowindex][VALIDATION_COMMENT_IND] = "Department and Sub-department are required when a Bracket is provided.";
      data[rowindex][UPLOAD_STATUS_IND] = undefined;
      data[rowindex][UPLAOD_COMMENTS_IND] = undefined;
      if (data[rowindex][START_DATE_IND] != undefined) {
        if (!(isNaN(data[rowindex][START_DATE_IND]))) {
          data[rowindex][START_DATE_IND] = tranformDate(data[rowindex][START_DATE_IND]);
        }
      }
      if (data[rowindex][END_DATE_IND] != undefined) {
        if (!(isNaN(data[rowindex][END_DATE_IND]))) {
          data[rowindex][END_DATE_IND] = tranformDate(data[rowindex][END_DATE_IND]);
        }
      }
    }
    else if (duplicates.includes(rowindex)) {
      validationFlag = false;
      data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
      if (validationStatusFlagSetter) validationStatusFlagSetter('Validation Failed');
      data[rowindex][VALIDATION_COMMENT_IND] = "This record is a duplicate of another record in this upload file";
      data[rowindex][UPLAOD_COMMENTS_IND] = undefined;
      data[rowindex][UPLOAD_STATUS_IND] = undefined;
      if (data[rowindex][START_DATE_IND] != undefined) {
        if (!(isNaN(data[rowindex][START_DATE_IND]))) {
          data[rowindex][START_DATE_IND] = tranformDate(data[rowindex][START_DATE_IND]);
        }
      }
      if (data[rowindex][END_DATE_IND] != undefined) {
        if (!(isNaN(data[rowindex][END_DATE_IND]))) {
          data[rowindex][END_DATE_IND] = tranformDate(data[rowindex][END_DATE_IND]);
        }
      }
    }
    else {
      let ruleValStatus: Boolean = true;
      /* Validate the Zone  */
      if (data[rowindex][ZONE_IND] === undefined || isNaN(data[rowindex][ZONE_IND]) || data[rowindex][ZONE_IND] < 0 || data[rowindex][ZONE_IND].toString().includes('.') || data[rowindex][ZONE_IND].toString().length > 5 || data[rowindex][ZONE_IND].toString().trim() === '') {
        validationFlag = false;
        data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
        data[rowindex][UPLOAD_STATUS_IND] = undefined;
        data[rowindex][UPLAOD_COMMENTS_IND] = undefined;
        if (data[rowindex][ZONE_IND] !== undefined) {
          if (data[rowindex][ZONE_IND].toString().trim() === '') {
            data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Zone Number is empty");
          }
          if (isNaN(data[rowindex][ZONE_IND])) {
            data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Invalid zone number");
          }
          /* Remove the validation for Zone exceeds 5 digit in remove sc. Yet to confirm with Anilesh to remove the condition here */
          else if (data[rowindex][ZONE_IND].toString().length > 5) {
            data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Zone number should not exceed 5 digits");
          }
          if (data[rowindex][ZONE_IND] < 0) {
            data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Zone Number should not be less than Zero");
          }
          if (data[rowindex][ZONE_IND].toString().includes('.')) {
            data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Zone Number should not be a decimal number");
          }
        }
        else
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Zone Number is required");
      }
      else {
        data[rowindex][VALIDATION_STATUS_IND] = 'Passed';
        data[rowindex][VALIDATION_COMMENT_IND] = undefined;
        data[rowindex][UPLOAD_STATUS_IND] = undefined;
        data[rowindex][UPLAOD_COMMENTS_IND] = undefined;
      }
      /* Validate the RMS number */
      if (isValidInput(data[rowindex][RMS_IND]) && isNaN(data[rowindex][RMS_IND])) {
        data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
        data[rowindex][UPLOAD_STATUS_IND] = undefined;
        data[rowindex][UPLAOD_COMMENTS_IND] = undefined;
        data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "RMS # is Invalid");
      }
      if (isValidInput(data[rowindex][SUB_DEPARTMENT_IND]) && isNaN(data[rowindex][SUB_DEPARTMENT_IND])) {
        validationFlag = false;
        data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Sub Department is Invalid");
      }
      if (isValidInput(data[rowindex][BRACKET_IND]) && isNaN(data[rowindex][BRACKET_IND])) {
        validationFlag = false;
        data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Bracket is Invalid");
      }
      if (isValidInput(data[rowindex][DEPARTMENT_IND]) && isNaN(data[rowindex][DEPARTMENT_IND])) {
        validationFlag = false;
        data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Department is Invalid");
      }

      /* Validate the criteria Effect type */
      if (isNotValidInput(data[rowindex][EFFECT_TYPE_IND]) || !(isNaN(data[rowindex][EFFECT_TYPE_IND]))) {
        validationFlag = false;
        data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Criteria Effective Type is mandatory and it is either 'Percentage' or  'Number'");
      }
      else if (!(CRITERIA_EFFECTIVE_TYPE.includes((data[rowindex][EFFECT_TYPE_IND])?.toUpperCase()))) {
        validationFlag = false;
        data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Criteria Effective Type is either 'Percentage' or  'Number'");
      }

      /* Validate the application rule order */
      if (isNotValidInput(data[rowindex][RULE_APP_ORDER_IND])) {
        validationFlag = false;

        data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Rule application order is mandatory");
      }
      else if (isValidInput(data[rowindex][RULE_APP_ORDER_IND]) && !(numRegex.test(data[rowindex][RULE_APP_ORDER_IND]))) {
        validationFlag = false;

        data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Rule application order should be a number start from 1");
      }

      /* Validate the brand Name */
      if (isValidInput(data[rowindex][BRAND_IND])) {


        if (!isNaN(data[rowindex][BRAND_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Brand Name not be a number");
        }

      }

      /* Validate start date */
      let startdate: any = undefined; // Store the Excel serial number, not Date object
      if (data[rowindex][START_DATE_IND] === undefined || isNaN(data[rowindex][START_DATE_IND]) || data[rowindex][START_DATE_IND] < 0 || data[rowindex][START_DATE_IND].toString().trim() === '') {
        validationFlag = false;

        data[rowindex][VALIDATION_STATUS_IND] = 'Failed';

        if (data[rowindex][START_DATE_IND] !== undefined) {
          console.log('data[rowindex][10]', data[rowindex][START_DATE_IND])
          console.log('data[rowindex][10]', isNaN(data[rowindex][START_DATE_IND]))
          if (data[rowindex][START_DATE_IND].toString().trim() === '') {
            data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Start Date is empty");
          }
          if (isNaN(data[rowindex][START_DATE_IND])) {
            data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Start Date is invalid");
          }
          if (data[rowindex][START_DATE_IND] < 0) {
            data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Start Date is invalid");
          }
        }
        else
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Start date is required");
      }
      else if (!(isNaN(data[rowindex][START_DATE_IND]))) {
        startdate = data[rowindex][START_DATE_IND];

        const uscurrentdate = new Date(new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' })).setHours(0, 0, 0, 0));
        const result = tranformDate(data[rowindex][START_DATE_IND]);
        data[rowindex][START_DATE_IND] = result
        console.log("us current date", uscurrentdate)
        if (new Date(uscurrentdate.toDateString()) > new Date(result.toDateString())) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Start date should not contain past dates");
        }
      }

      /* Validate end date */
      // Feature Flag: When disableSecondaryCriteriaPublish is '0', END_DATE validation is performed
      // If END_DATE is not filled by user, skip all End Date validations
      if (disableSecondaryCriteriaPublish === '0') {
        // Check if END_DATE is provided by user
        const endDateProvided = data[rowindex][END_DATE_IND] !== undefined &&
          data[rowindex][END_DATE_IND] !== null &&
          data[rowindex][END_DATE_IND].toString().trim() !== '';

        // Only validate if END_DATE is provided by user
        if (endDateProvided) {
          if (isNaN(data[rowindex][END_DATE_IND]) || data[rowindex][END_DATE_IND] < 0) {
            validationFlag = false;
            data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
            if (isNaN(data[rowindex][END_DATE_IND])) {
              data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "End Date is invalid");
            }
            if (data[rowindex][END_DATE_IND] < 0) {
              data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "End Date is invalid");
            }
          }
          else if (!(isNaN(data[rowindex][END_DATE_IND]))) {
            const enddate = data[rowindex][END_DATE_IND];
            const endResult = tranformDate(data[rowindex][END_DATE_IND]);
            data[rowindex][END_DATE_IND] = endResult;

            // Validate end date is not beyond 12/31/2099
            // Create max date using date components to avoid timezone issues
            const maxDate = new Date(2099, 11, 31); // Month is 0-indexed, so 11 = December
            const endDateOnly = new Date(endResult.getFullYear(), endResult.getMonth(), endResult.getDate());
            const maxDateOnly = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
            if (endDateOnly > maxDateOnly) {
              validationFlag = false;
              data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
              data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "End date should not be beyond 12/31/2099");
            }
            const uscurrentdate = new Date(new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' })).setHours(0, 0, 0, 0));
            if (new Date(uscurrentdate.toDateString()) > new Date(endResult.toDateString())) {
              validationFlag = false;
              data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
              data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "End date should not contain past dates");
            }

            // Validate end date is on or after start date (can be equal)
            // Only compare if both startdate and enddate are valid Excel serial numbers
            if (startdate !== undefined && enddate !== undefined) {
              const startResult = tranformDate(startdate);
              if (new Date(endResult.toDateString()) < new Date(startResult.toDateString())) {
                validationFlag = false;
                data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
                data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "End date should be on or after Start date");
              }
            }
          }
        }
        // If END_DATE is not provided, skip all validations (no error)
      }

      /* validate the rule type */
      if (isValidInput(data[rowindex][RULE_TYPE_IND])) {
        if (!isNaN(data[rowindex][RULE_TYPE_IND])) {
          validationFlag = false;
          ruleValStatus = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Rule type should not a number");
        }
        else if (showFeature?.equAccess == false && transformText(data[rowindex][RULE_TYPE_IND]) == 'equation') {
          validationFlag = false;
          ruleValStatus = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Create and Update of Equation rule type is currently restricted to this user");
        }
        else if (!(referenceData.ruleApplicationType.includes(transformText(data[rowindex][RULE_TYPE_IND])))) {
          ruleValStatus = false;
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Rule type is invalid");
        }
      }
      else {
        validationFlag = false;
        ruleValStatus = false;
        data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Rule type is mandatory");
      }
      /* Validate the pricing paramter */
      if (isValidInput(data[rowindex][PRICE_PARAM_IND])) {
        if (!isNaN(data[rowindex][PRICE_PARAM_IND])) {
          validationFlag = false;

          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Pricing parameter should not be a number");
        }
        else if (!(referenceData.pricingParamKey.includes(transformText(data[rowindex][PRICE_PARAM_IND])))) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Price Paramter is invalid");
        }
        /*         else{
                  if(transformText(data[rowindex][PRICE_PARAM_IND]) == 'sacdays'  && (data[rowindex][EFFECT_TYPE_IND]).toUpperCase() == CRITERIA_EFFECTIVE_TYPE[1]){
                    validationFlag = false;
                  data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
                    data[rowindex][VALIDATION_COMMENT_IND] = (data[rowindex][VALIDATION_COMMENT_IND] == undefined ? "Criteria Effective Type shoule be 'Percentage' when Price Parameter is 'SAC Days'" : data[rowindex][VALIDATION_COMMENT_IND] + ", Criteria Effective Type shoule be 'Percentage' when Price Parameter is 'SAC Days'");
                  }
                  if(transformText(data[rowindex][PRICE_PARAM_IND]) == 'epopercent'  && (data[rowindex][EFFECT_TYPE_IND]).toUpperCase() == CRITERIA_EFFECTIVE_TYPE[0]){
                    validationFlag = false;
                  data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
                    data[rowindex][VALIDATION_COMMENT_IND] = (data[rowindex][VALIDATION_COMMENT_IND] == undefined ? "Criteria Effective Type shoule be 'Number' when Price Parameter is 'EPO Percent'" : data[rowindex][VALIDATION_COMMENT_IND] + ", Criteria Effective Type shoule be 'Number' when Price Parameter is 'EPO Percent'");
                  }
                } */

      }
      else {
        validationFlag = false;
        data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Price parameter is mandatory");
      }

      /* Secondary criteria validation */
      if (isValidInput(data[rowindex][CRITERIA_TYPE_IND])) {
        if (!isNaN(data[rowindex][RULE_TYPE_IND])) {
          validationFlag = false;

          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Secondary criteria Type should not be a number");
        }
        else if (!(referenceData.secondaryCriteriaType.includes(transformText(data[rowindex][CRITERIA_TYPE_IND])))) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Secondary criteria Type is invalid");
        }
        if ((isNaN(data[rowindex][CRITERIA_TYPE_IND])) && (data[rowindex][CRITERIA_TYPE_IND])?.toUpperCase() == 'CATEGORY' && (data[rowindex][RULE_TYPE_IND])?.toUpperCase() != 'CATEGORY') {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "rule Type shoule be 'CATEGORY' when Secondary Criteria Type is 'CATEGORY'");
        }

      }
      else {
        validationFlag = false;
        data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Secondary Criteria Type is mandatory");
      }


      /* Validate based on the rule type 'TABLE'*/
      if (ruleValStatus && isValidInput(data[rowindex][RULE_TYPE_IND]) && isNaN(data[rowindex][RULE_TYPE_IND]) && (data[rowindex][RULE_TYPE_IND])?.toUpperCase() == 'TABLE') {
        validateDiscount();
        let daysonrentstart = -1, daysonrentend = -1;
        //Days on Rent Start
        if (data[rowindex][START_RANGE_IND] === undefined || isNaN(data[rowindex][START_RANGE_IND]) || data[rowindex][START_RANGE_IND] < 0 || data[rowindex][START_RANGE_IND].toString().includes('.') ||
          data[rowindex][START_RANGE_IND].toString().trim() === '') {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          if (data[rowindex][START_RANGE_IND] !== undefined) {
            if (data[rowindex][START_RANGE_IND].toString().trim() === '') {
              data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Start Range is empty");
            }
            if (isNaN(data[rowindex][START_RANGE_IND])) {
              data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Invalid Start Range");
            }
            if (data[rowindex][START_RANGE_IND] < 0) {
              data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Start Range should not be less than Zero");
            }
            if (data[rowindex][START_RANGE_IND].toString().includes('.')) {
              data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Start Range should not be a decimal number");
            }
          }
          else
            data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Start Range is required");
        }
        else {
          daysonrentstart = data[rowindex][START_RANGE_IND];
        }


        if (data[rowindex][END_RANGE_IND] === undefined || isNaN(data[rowindex][END_RANGE_IND]) || data[rowindex][END_RANGE_IND] < 0 || data[rowindex][END_RANGE_IND].toString().includes('.') ||
          data[rowindex][END_RANGE_IND].toString().trim() === '') {

          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';

          if (data[rowindex][END_RANGE_IND] !== undefined) {
            if (data[rowindex][END_RANGE_IND].toString().trim() === '') {
              data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "End Range is empty");
            }
            if (isNaN(data[rowindex][END_RANGE_IND])) {
              data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Invalid End Range");
            }
            if (data[rowindex][END_RANGE_IND] < 0) {
              data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "End Range should not be less than Zero");
            }
            if (data[rowindex][END_RANGE_IND].toString().includes('.')) {
              data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "End Range should not be a decimal number");
            }
          }
          else
            data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "End Range is required");
        }
        else {
          daysonrentend = data[rowindex][END_RANGE_IND];
        }
        if (daysonrentstart != -1 && daysonrentend != -1) {
          if (daysonrentstart > daysonrentend) {
            validationFlag = false;
            data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
            data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "End Range should be greater than Start Range ");
          }
        }
        /* Validate the Equation type and param , category type and sub type are empty*/
        if (isValidInput(data[rowindex][EQUATION_TYPE_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Equation Type is not needed when rule type is 'TABLE'");

        }
        if (isValidInput(data[rowindex][EQUATION_PARAM_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Equation key is not needed when rule type is 'TABLE'");
        }
        if (isValidInput(data[rowindex][CATEGORY_TYPE_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Category Type is not needed when rule type is 'TABLE'");
        }
        if (isValidInput(data[rowindex][CATEGORY_SUBTYPE_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Category Subtype is not needed when rule type is 'TABLE'");
        }
      }

      /* Validate based on the rule type 'EQUATION'*/
      if (ruleValStatus && isValidInput(data[rowindex][RULE_TYPE_IND]) && isNaN(data[rowindex][RULE_TYPE_IND]) && (data[rowindex][RULE_TYPE_IND]).toUpperCase() == 'EQUATION') {

        /* Validate The Equation Type */
        if (isNotValidInput(data[rowindex][EQUATION_TYPE_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Equation Type is mandatory when rule type is'EQUATION'");
        }
        else if (!(isNaN(data[rowindex][EQUATION_TYPE_IND]))) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Equation Type should not be a number");
        }
        else {
          /* Validate the Equation type with reference */
          if (referenceData.equationType.includes(transformText(data[rowindex][EQUATION_TYPE_IND]))) {
            const element = referenceData.equationParameter.find((item: any) =>
              transformText(item.equationType) == transformText(data[rowindex][EQUATION_TYPE_IND])
            )
            if (isNotValidInput(element)) {
              validationFlag = false;
              data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
              data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Equation Type is Invalid");
            } else {
              /* Validate the eqution param is sub set of equation type */
              if (element && element.param != null) {
                validateDiscount();
                if (isValidInput(data[rowindex][EQUATION_PARAM_IND])) {
                  const param = element.param.find((item: any) => (item)?.toLowerCase() == data[rowindex][EQUATION_PARAM_IND]?.toLowerCase());
                  if (isNotValidInput(param)) {
                    validationFlag = false;
                    data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
                    data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Equation Key is not associate with the Equation Type");
                  }
                }
              }
              /* When no parameter exist for the equation then disocunt value should be empty */
              else {
                if (isValidInput(data[rowindex][DISCOUNT_VALUE_IND])) {
                  validationFlag = false;
                  data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
                  data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], `Discount Value shouldn't exist for this equation type`);
                }
              }
            }

          }
          else {
            validationFlag = false;
            data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
            data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Equation Type is Invalid");
          }
        }

        /* Validate the Equation param */
        if (isNotValidInput(data[rowindex][EQUATION_PARAM_IND]) && (isNaN(data[rowindex][RULE_TYPE_IND])) && (data[rowindex][RULE_TYPE_IND].toUpperCase() == 'EQUATION')) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Equation Key is mandatory when rule type is'EQUATION'");
        }
        else if (!(isNaN(data[rowindex][EQUATION_PARAM_IND]))) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Equation Key  should not be a number");
        }



        if (isValidInput(data[rowindex][START_RANGE_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Start Range is not needed when rule type is 'EQUATION'");

        }
        if (isValidInput(data[rowindex][END_RANGE_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "End Range is not needed when rule type is 'EQUATION'");
        }
        if (isValidInput(data[rowindex][CATEGORY_TYPE_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Category Type is not needed when rule type is 'EQUATION'");
        }
        if (isValidInput(data[rowindex][CATEGORY_SUBTYPE_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Category Subtype is not needed when rule type is 'EQUATION'");
        }
      }


      /* validation based on the rule type 'CATEGORY' */
      if (ruleValStatus && isValidInput(data[rowindex][RULE_TYPE_IND]) && isNaN(data[rowindex][RULE_TYPE_IND]) && (data[rowindex][RULE_TYPE_IND]).toUpperCase() == 'CATEGORY') {
        validateDiscount();

        if ((data[rowindex][RULE_TYPE_IND]).toUpperCase() != 'CATEGORY') {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Secondary Criteria Type shoule be 'CATEGORY' when rule type is 'CATEGORY'");
        }
        /* Validate The category Type */
        if (isNotValidInput(data[rowindex][CATEGORY_TYPE_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Category Type is mandatory when rule type is'CATEGORY'");
        }
        else if (!(isNaN(data[rowindex][CATEGORY_TYPE_IND]))) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Category Type should not be a number");
        }
        else {
          if (!(referenceData.categoryType.includes(transformText(data[rowindex][CATEGORY_TYPE_IND])))) {
            validationFlag = false;
            data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
            data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Category type is invalid");

          }
        }

        /* Validate the category Sub Type */
        if (isNotValidInput(data[rowindex][CATEGORY_SUBTYPE_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Category Subtype is mandatory when rule type is'CATEGORY'");
        }
        else if (!(isNaN(data[rowindex][CATEGORY_SUBTYPE_IND]))) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Category Subtype should not be a number");
        }
        else {
          if (!(referenceData.categorySubType.includes(transformText(data[rowindex][CATEGORY_SUBTYPE_IND])))) {
            validationFlag = false;
            data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
            data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Category Subtype is invalid");

          }
        }

        if (isValidInput(data[rowindex][EQUATION_TYPE_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Equation Type is not needed when rule type is 'CATEGORY'");

        }
        if (isValidInput(data[rowindex][EQUATION_PARAM_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Equation key is not needed when rule type is 'CATEGORY'");
        }
        if (isValidInput(data[rowindex][START_RANGE_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "Start Range is not needed when rule type is 'CATEGORY'");

        }
        if (isValidInput(data[rowindex][END_RANGE_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_IND], "End Range is not needed when rule type is 'CATEGORY'");
        }
      }

    }
  }
  if (validationFlag) {
    const header = data.shift()
    const groupedByrule = await groupAndOrderRows(data, indices);
    const groupedData: any[] = [[header]];
    console.log('The group by hierarvhy ', groupedByrule);
    for (let [ruleTypeKey, records] of Object.entries(groupedByrule)) {
      console.log('ruleTypeKey', ruleTypeKey)
      switch (ruleTypeKey.toLowerCase()) {
        case 'table':
          {
            // Build keys array - include END_DATE_IND only if feature flag is enabled
            const keys: number[] = [ZONE_IND, DEPARTMENT_IND, SUB_DEPARTMENT_IND, BRACKET_IND, BRAND_IND, RMS_IND, RULE_TYPE_IND, RULE_APP_ORDER_IND, CRITERIA_TYPE_IND, PRICE_PARAM_IND, EFFECT_TYPE_IND, START_DATE_IND];
            if (disableSecondaryCriteriaPublish === '0') {
              keys.push(END_DATE_IND);
            }

            const groupedTableRec = await groupingHierarchy(records, keys);
            console.log('groupedTableRec', groupedTableRec)
            for (let [, rows] of Object.entries(groupedTableRec) as [string, any[]][]) {
              const resp = await validateTableRecords(rows, indices, validationStatusFlagSetter)
              console.log('THe end response')
              groupedData.push(resp);

            }
            break;

          }
        case 'equation':
          {
            // Build keys array - include END_DATE_IND only if feature flag is enabled
            const keys = [ZONE_IND, DEPARTMENT_IND, SUB_DEPARTMENT_IND, BRACKET_IND, BRAND_IND, RMS_IND, RULE_TYPE_IND, RULE_APP_ORDER_IND, CRITERIA_TYPE_IND, PRICE_PARAM_IND, EFFECT_TYPE_IND, START_DATE_IND];
            if (disableSecondaryCriteriaPublish === '0') {
              keys.push(END_DATE_IND);
            }
            keys.push(EQUATION_TYPE_IND);

            const hierarchyGroupData = await groupingHierarchy(records, keys);
            console.log('The hierarchy group data equation', hierarchyGroupData)
            for (const [, subGroupRecords] of Object.entries(hierarchyGroupData) as [string, any[]][]) {

              const resp = await validateEquationParameters(subGroupRecords, indices, referenceData, validationStatusFlagSetter);
              console.log('THe equation response', resp)
              groupedData.push(resp);

            }
            break;
          }
        default: {
          console.log('The gp records', records)
          groupedData.push(records);
          break;
        }
      }
    }
    console.log('Grouped records data', groupedData.flat())

    return groupedData.flat()
  }
  else {
    validationStatusFlagSetter('Validation Failed')
  }

  return data
};
